import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  ChefHat,
  ChevronRight,
  Clock3,
  Droplets,
  Flame,
  Flower2,
  Heart,
  Home,
  Info,
  Leaf,
  Mail,
  MapPin,
  Menu,
  Phone,
  RotateCcw,
  ShieldCheck,
  ShoppingBasket,
  Sparkles,
  Sunrise,
  Timer,
  Utensils,
  Wind,
  X,
} from "lucide-react";
import { calculateProfile, DOSHA_META, formatDoshas, saveProfile } from "./lib";
import { useI18n } from "./i18n";
import { PeopleStepper, ShoppingPlanner } from "./ShoppingPlanner";
import { loadShoppingState, saveShoppingState, type StoredShoppingState } from "./shopping";
import { loadPantry, pantryCoverage } from "./coach";
import type { Dosha, DoshaProfile, LegalNotice, Question, Recipe, Release, Ritual, View } from "./types";

const nav: { id: View; label: string; labelKey: string; icon: typeof Home }[] = [
  { id: "home", label: "Start", labelKey: "nav.home", icon: Home },
  { id: "coach", label: "Heute", labelKey: "nav.coach", icon: Sparkles },
  { id: "quiz", label: "Dosha-Test", labelKey: "nav.quiz", icon: Sparkles },
  { id: "recipes", label: "Rezepte", labelKey: "nav.recipes", icon: ChefHat },
  { id: "rituals", label: "Rituale", labelKey: "nav.rituals", icon: Sunrise },
  { id: "about", label: "Mehr", labelKey: "nav.about", icon: Info },
];

const mobileNavIds: View[] = ["home", "coach", "recipes", "rituals"];

export function Header({ view, onNavigate }: { view: View; onNavigate: (view: View) => void }) {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <header className="site-header">
        <button className="brand" onClick={() => onNavigate("home")} aria-label="Ayurvedon Startseite">
          <img src="/favicon-64.png" alt="" width="42" height="42" />
          <span>Ayurvedon</span>
        </button>
        <nav className="desktop-nav" aria-label="Hauptnavigation">
          {nav.map((item) => (
            <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => onNavigate(item.id)}>
              {t(item.labelKey, item.label)}
            </button>
          ))}
        </nav>
        <button className="menu-button" onClick={() => setOpen(true)} aria-label="Menü öffnen"><Menu /></button>
      </header>
      {open && (
        <div className="menu-sheet" role="dialog" aria-modal="true" aria-label="Navigation">
          <button className="menu-close" onClick={() => setOpen(false)} aria-label="Menü schließen"><X /></button>
          <p className="eyebrow">Navigation</p>
          {nav.map((item) => (
            <button key={item.id} onClick={() => { onNavigate(item.id); setOpen(false); }}>
              <item.icon size={20} /> {t(item.labelKey, item.label)}<ChevronRight size={18} />
            </button>
          ))}
        </div>
      )}
      <nav className="mobile-nav" aria-label="Mobile Navigation">
        {nav.filter((item) => mobileNavIds.includes(item.id)).map((item) => (
          <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => onNavigate(item.id)}>
            <item.icon size={20} strokeWidth={1.8} /><span>{t(item.labelKey, item.label)}</span>
          </button>
        ))}
      </nav>
    </>
  );
}

function DoshaGlyph({ dosha }: { dosha: Dosha }) {
  const Icon = dosha === "vata" ? Wind : dosha === "pitta" ? Flame : Droplets;
  return <Icon aria-hidden="true" />;
}

export function ProfileCard({ profile, compact = false }: { profile: DoshaProfile; compact?: boolean }) {
  const t = useI18n();
  const primary = DOSHA_META[profile.primary];
  return (
    <article className={`profile-card ${compact ? "compact" : ""}`} style={{ "--dosha": primary.color } as React.CSSProperties}>
      <div className="profile-icon"><DoshaGlyph dosha={profile.primary} /></div>
      <div>
        <p className="eyebrow">{t("profile.primary", "Dein aktueller Schwerpunkt")}</p>
        <h2>{primary.label}{profile.secondary ? `–${DOSHA_META[profile.secondary].label}` : ""}</h2>
        <p>{primary.essence}</p>
      </div>
      {!compact && (
        <div className="score-row" aria-label="Dosha-Punkte">
          {(Object.keys(profile.scores) as Dosha[]).map((dosha) => (
            <span key={dosha}><b>{DOSHA_META[dosha].label}</b>{profile.scores[dosha]}</span>
          ))}
        </div>
      )}
    </article>
  );
}

export function Questionnaire({
  questions,
  version,
  onComplete,
  onCancel,
}: {
  questions: Question[];
  version: string;
  onComplete: (profile: DoshaProfile) => void;
  onCancel: () => void;
}) {
  const t = useI18n();
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<DoshaProfile | null>(null);
  const question = questions[index];
  const selected = question ? answers[question.id] : undefined;
  const progress = ((index + 1) / questions.length) * 100;

  useEffect(() => window.scrollTo({ top: 0, behavior: "smooth" }), [index, result]);

  if (!questions.length) return <div className="empty-state">Der Fragebogen wird vorbereitet.</div>;

  if (result) {
    const primary = DOSHA_META[result.primary];
    return (
      <main className="result-page page-shell">
        <button className="back-link" onClick={onCancel}><ArrowLeft size={18} /> {t("common.backOverview", "Zur Übersicht")}</button>
        <div className="result-orbit" style={{ "--dosha": primary.color } as React.CSSProperties}>
          <DoshaGlyph dosha={result.primary} />
        </div>
        <p className="eyebrow">{t("quiz.reflection", "Deine Reflexion")}</p>
        <h1>{primary.label}{result.secondary ? ` trifft ${DOSHA_META[result.secondary].label}` : ""}</h1>
        <p className="result-lead">{primary.essence}. {t("quiz.resultIntro", "Dein Ergebnis ist eine Einladung, dich bewusster zu beobachten – keine starre Schublade.")}</p>
        <ProfileCard profile={result} />
        <div className="notice"><ShieldCheck /><p><b>{t("common.goodToKnow", "Gut zu wissen")}</b>{t("quiz.disclaimerLong", "Dieser Selbsttest ist nicht medizinisch validiert und ersetzt weder Diagnose noch Behandlung. Bei Beschwerden wende dich bitte an eine qualifizierte Fachperson.")}</p></div>
        <div className="button-row">
          <button className="primary-button" onClick={() => onComplete(result)}>{t("quiz.recommendations", "Meine Empfehlungen ansehen")} <ArrowRight /></button>
          <button className="secondary-button" onClick={() => { setAnswers({}); setIndex(0); setResult(null); }}><RotateCcw /> {t("quiz.restart", "Test wiederholen")}</button>
        </div>
      </main>
    );
  }

  const finish = () => {
    const profile = calculateProfile(questions, answers, version);
    saveProfile(profile);
    setResult(profile);
  };

  return (
    <main className="quiz-page">
      <div className="quiz-topbar">
        <button onClick={onCancel} aria-label="Test verlassen"><X /></button>
        <div><span>{t("quiz.progress", "{current} von {total}", { current: index + 1, total: questions.length })}</span><div className="progress"><i style={{ width: `${progress}%` }} /></div></div>
        <span className="quiz-time"><Clock3 size={15} /> ca. {Math.max(1, Math.ceil((questions.length - index) * 0.25))} Min.</span>
      </div>
      <section className="question-card" key={question.id}>
        <p className="eyebrow">{question.category}</p>
        <h1>{question.prompt}</h1>
        {question.help && <p className="question-help">{question.help}</p>}
        <div className="option-list" role="radiogroup" aria-label={question.prompt}>
          {question.options.map((option, optionIndex) => (
            <button
              key={option.label}
              className={selected === optionIndex ? "selected" : ""}
              role="radio"
              aria-checked={selected === optionIndex}
              onClick={() => setAnswers((current) => ({ ...current, [question.id]: optionIndex }))}
            >
              <i>{String.fromCharCode(65 + optionIndex)}</i>
              <span><b>{option.label}</b><small>{option.description}</small></span>
              <em><Check /></em>
            </button>
          ))}
        </div>
      </section>
      <div className="quiz-actions">
          <button className="secondary-button" disabled={index === 0} onClick={() => setIndex((value) => value - 1)}><ArrowLeft /> {t("quiz.back", "Zurück")}</button>
        {index < questions.length - 1 ? (
          <button className="primary-button" disabled={selected === undefined} onClick={() => setIndex((value) => value + 1)}>{t("quiz.next", "Weiter")} <ArrowRight /></button>
        ) : (
          <button className="primary-button" disabled={selected === undefined} onClick={finish}>{t("quiz.result", "Ergebnis ansehen")} <Sparkles /></button>
        )}
      </div>
    </main>
  );
}

export function RecipeLibrary({ recipes, profile }: { recipes: Recipe[]; profile: DoshaProfile | null }) {
  const t = useI18n();
  const [filter, setFilter] = useState<Dosha | "all">(profile?.primary ?? "all");
  const [selected, setSelected] = useState<Recipe | null>(null);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [shopping, setShopping] = useState<StoredShoppingState>(() => loadShoppingState());
  const [pantryKeys, setPantryKeys] = useState<string[]>(() => loadPantry());
  const filtered = useMemo(() => {
    const matches = filter === "all" ? recipes : recipes.filter((recipe) => recipe.doshas.includes(filter));
    const pantry = new Set(pantryKeys);
    return pantry.size ? [...matches].sort((left, right) => pantryCoverage(right, pantry) - pantryCoverage(left, pantry)) : matches;
  }, [filter, recipes, pantryKeys]);
  const selectedRecipes = useMemo(() => new Map(shopping.selections.map((selection) => [selection.recipeSlug, selection])), [shopping.selections]);

  useEffect(() => saveShoppingState(shopping), [shopping]);
  useEffect(() => {
    const refresh = () => setPantryKeys(loadPantry());
    window.addEventListener("ayurvedon:pantry-changed", refresh);
    return () => window.removeEventListener("ayurvedon:pantry-changed", refresh);
  }, []);

  const selectRecipe = (recipe: Recipe) => {
    setShopping((current) => current.selections.some((item) => item.recipeSlug === recipe.slug)
      ? current
      : { ...current, selections: [...current.selections, { recipeSlug: recipe.slug, people: recipe.servings }] });
  };

  const removeRecipe = (slug: string) => {
    if (shopping.selections.length === 1) setPlannerOpen(false);
    setShopping((current) => ({ ...current, selections: current.selections.filter((item) => item.recipeSlug !== slug) }));
  };
  const setPeople = (slug: string, people: number) => setShopping((current) => ({
    ...current,
    selections: current.selections.map((item) => item.recipeSlug === slug ? { ...item, people } : item),
  }));

  return (
    <main className="page-shell library-page">
      <p className="eyebrow">{t("recipes.eyebrow", "Mit Wärme genährt")}</p>
      <h1>{t("recipes.title", "Rezepte für deinen Rhythmus")}</h1>
      <p className="page-intro">{t("recipes.intro", "Eigenständig entwickelte, pflanzenbetonte Alltagsküche – sanft gewürzt, klar beschrieben und immer mit Allergenhinweisen.")}</p>
      <div className="filter-chips" role="group" aria-label="Rezepte filtern">
        {(["all", "vata", "pitta", "kapha"] as const).map((dosha) => (
          <button key={dosha} className={filter === dosha ? "active" : ""} onClick={() => setFilter(dosha)}>
            {dosha === "all" ? t("common.all", "Alle") : DOSHA_META[dosha].label}
          </button>
        ))}
      </div>
      <div className="card-grid">
        {filtered.map((recipe) => {
          const planSelection = selectedRecipes.get(recipe.slug);
          return <article className={`recipe-card ${planSelection ? "planned" : ""}`} key={recipe.slug}>
            <div className="recipe-image"><img src={recipe.image} alt="" loading="lazy" /><span>{formatDoshas(recipe.doshas)}</span></div>
            <div className="recipe-body">
              <p className="eyebrow">{recipe.season}</p><h2>{recipe.title}</h2><p>{recipe.subtitle}</p>
              <div className="meta-row"><span><Timer /> {recipe.minutes} Min.</span><span><Utensils /> {recipe.servings} Port.</span></div>
              <div className="recipe-links"><button className="text-button" onClick={() => setSelected(recipe)}>{t("recipes.open", "Rezept öffnen")} <ArrowRight /></button></div>
              {planSelection ? (
                <div className="card-plan-control">
                  <span><Check /> {t("shopping.selected", "Ausgewählt")}</span>
                  <PeopleStepper value={planSelection.people} onChange={(people) => setPeople(recipe.slug, people)} compact />
                  <button onClick={() => removeRecipe(recipe.slug)} aria-label={t("shopping.removeRecipe", "{recipe} entfernen", { recipe: recipe.title })}><X /></button>
                </div>
              ) : (
                <button className="add-to-plan" onClick={() => selectRecipe(recipe)}><ShoppingBasket /> {t("shopping.add", "Zur Einkaufsliste")}</button>
              )}
            </div>
          </article>;
        })}
      </div>
      {selected && <RecipeDetail recipe={selected} onClose={() => setSelected(null)} />}
      {!!shopping.selections.length && !plannerOpen && (
        <button className="shopping-fab" onClick={() => setPlannerOpen(true)}>
          <i><ShoppingBasket /><b>{shopping.selections.length}</b></i>
          <span><strong>{t("shopping.title", "Einkaufsliste")}</strong><small>{shopping.selections.length === 1 ? t("shopping.oneRecipe", "1 Rezept ausgewählt") : t("shopping.recipeCount", "{count} Rezepte ausgewählt", { count: shopping.selections.length })}</small></span>
          <ArrowRight />
        </button>
      )}
      {plannerOpen && (
        <ShoppingPlanner
          recipes={recipes}
          selections={shopping.selections}
          checkedIds={new Set(shopping.checked)}
          onPeopleChange={setPeople}
          onRemoveRecipe={removeRecipe}
          onToggleItem={(id) => setShopping((current) => ({ ...current, checked: current.checked.includes(id) ? current.checked.filter((item) => item !== id) : [...current.checked, id] }))}
          onClearChecked={() => setShopping((current) => ({ ...current, checked: [] }))}
          onClearAll={() => { setShopping({ selections: [], checked: [] }); setPlannerOpen(false); }}
          onClose={() => setPlannerOpen(false)}
        />
      )}
    </main>
  );
}

function RecipeDetail({ recipe, onClose }: { recipe: Recipe; onClose: () => void }) {
  const t = useI18n();
  useEffect(() => { document.body.classList.add("modal-open"); return () => document.body.classList.remove("modal-open"); }, []);
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <article className="detail-sheet" role="dialog" aria-modal="true" aria-labelledby="recipe-title">
        <button className="modal-close" onClick={onClose} aria-label="Rezept schließen"><X /></button>
        <img className="detail-hero" src={recipe.image} alt="" />
        <div className="detail-content">
          <p className="eyebrow">{formatDoshas(recipe.doshas)} · {recipe.season}</p><h1 id="recipe-title">{recipe.title}</h1><p className="page-intro">{recipe.intro}</p>
          <div className="detail-facts"><span><Timer /> {recipe.minutes} Minuten</span><span><Utensils /> {recipe.servings} Portionen</span><span><ChefHat /> {recipe.difficulty}</span></div>
          <div className="recipe-columns">
            <section><h2>{t("recipes.ingredients", "Zutaten")}</h2><ul className="ingredient-list">{recipe.ingredients.map((item) => <li key={item}>{item}</li>)}</ul></section>
            <section><h2>{t("recipes.steps", "Zubereitung")}</h2><ol className="step-list">{recipe.steps.map((item, index) => <li key={item}><i>{index + 1}</i><span>{item}</span></li>)}</ol></section>
          </div>
          <div className="allergen-box"><ShieldCheck /><p><b>{t("recipes.allergens", "Allergene")}</b>{recipe.allergens.length ? recipe.allergens.join(", ") : t("recipes.noAllergens", "Keine der 14 Hauptallergene in der Grundrezeptur. Produktetiketten dennoch prüfen.")}</p></div>
          <p className="editorial-note"><b>{t("recipes.ayurvedaNote", "Ayurvedische Einordnung:")}</b> {recipe.note}</p>
        </div>
      </article>
    </div>
  );
}

const ritualIcons = { sunrise: Sunrise, leaf: Leaf, heart: Heart, flower: Flower2, book: BookOpen, wind: Wind };

export function RitualLibrary({ rituals, profile }: { rituals: Ritual[]; profile: DoshaProfile | null }) {
  const t = useI18n();
  const ordered = useMemo(() => [...rituals].sort((a, b) => Number(b.doshas.includes(profile?.primary ?? "vata")) - Number(a.doshas.includes(profile?.primary ?? "vata"))), [rituals, profile]);
  const [selected, setSelected] = useState<Ritual | null>(null);
  return (
    <main className="page-shell library-page ritual-page">
      <p className="eyebrow">{t("rituals.eyebrow", "Kleine Anker im Alltag")}</p><h1>{t("rituals.title", "Rituale, die Raum schaffen")}</h1>
      <p className="page-intro">{t("rituals.intro", "Keine Selbstoptimierung. Nur bewusst gesetzte Pausen, die du an deinen Alltag und dein Wohlbefinden anpassen kannst.")}</p>
      <div className="ritual-grid">
        {ordered.map((ritual) => {
          const Icon = ritualIcons[ritual.icon as keyof typeof ritualIcons] ?? Leaf;
          return <article className="ritual-card" key={ritual.slug}>
            <div className="ritual-icon"><Icon /></div><p className="eyebrow">{ritual.eyebrow}</p><h2>{ritual.title}</h2><p>{ritual.intro}</p>
            <div className="meta-row"><span><Clock3 /> {ritual.duration}</span><span>{ritual.moment}</span></div>
            <button className="text-button" onClick={() => setSelected(ritual)}>{t("rituals.open", "Ritual ansehen")} <ArrowRight /></button>
          </article>;
        })}
      </div>
      {selected && (
        <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setSelected(null)}>
          <article className="detail-sheet compact-sheet" role="dialog" aria-modal="true">
            <button className="modal-close" onClick={() => setSelected(null)} aria-label="Ritual schließen"><X /></button>
            <div className="detail-content"><p className="eyebrow">{selected.eyebrow} · {selected.duration}</p><h1>{selected.title}</h1><p className="page-intro">{selected.intro}</p>
              <ol className="step-list">{selected.steps.map((step, index) => <li key={step}><i>{index + 1}</i><span>{step}</span></li>)}</ol>
              {selected.caution && <div className="notice"><Info /><p><b>{t("rituals.caution", "Achtsam anwenden")}</b>{selected.caution}</p></div>}
            </div>
          </article>
        </div>
      )}
    </main>
  );
}

export function AboutPage({ releases, version, onClear }: { releases: Release[]; version: string; onClear: () => void }) {
  const t = useI18n();
  const clearLocalData = () => {
    if (window.confirm(t("profile.clearConfirm", "Profil, Routinen, Check-ins, Vorrat, Wochenplan und Einkaufslisten auf diesem Gerät wirklich löschen?"))) onClear();
  };
  return (
    <main className="page-shell about-page">
      <p className="eyebrow">{t("about.eyebrow", "Transparent & sorgfältig")}</p><h1>{t("about.title", "Über Ayurvedon")}</h1>
      <div className="about-grid">
        <section className="prose-card"><h2>{t("about.disclaimer.title", "Ein Begleiter, kein Behandler")}</h2><p>{t("about.disclaimer.body", "Ayurvedon übersetzt traditionelle Ayurveda-Konzepte in ruhige, alltagstaugliche Reflexionen. Der Dosha-Test ist nicht wissenschaftlich validiert und stellt keine Diagnose dar. Rezepte und Rituale ersetzen keine medizinische oder ernährungstherapeutische Beratung.")}</p><p>{t("about.disclaimer.caution", "Bei anhaltenden Beschwerden, Schwangerschaft, Erkrankungen, Allergien oder Medikamenteneinnahme kläre Änderungen bitte mit einer qualifizierten Fachperson.")}</p></section>
        <section className="prose-card"><h2>{t("about.privacy.title", "Datenschutz by Design")}</h2><p>{t("about.privacy.body", "Dein Profil und deine Coach-Daten werden lokal gespeichert. Nur die freiwillige Wetterfunktion sendet Ortssuche oder Koordinaten an Open-Meteo. Du kannst alle lokalen Daten hier löschen.")}</p><button className="secondary-button danger" onClick={clearLocalData}><RotateCcw /> {t("profile.clear", "Alle lokalen Daten löschen")}</button><a className="text-button privacy-link" href="#privacy">{t("about.privacy.details", "Datenschutzerklärung lesen")} <ArrowRight /></a></section>
      </div>
      <section className="sources"><h2>{t("about.sources.title", "Redaktionelle Grundlage")}</h2><p>{t("about.sources.body", "Alle Rezepte dieser Erstversion wurden für Ayurvedon eigenständig entwickelt. Die Auswahl setzt auf überwiegend wenig verarbeitete, pflanzenbetonte Lebensmittel. Allergene werden sichtbar ausgewiesen. Vor einer öffentlichen Veröffentlichung ist eine fachliche Endprüfung vorgesehen.")}</p></section>
      <section className="changelog"><p className="eyebrow">{t("about.version", "Aktuelle App-Version {version}", { version })}</p><h2>{t("about.changelog.title", "Was neu ist")}</h2>
        {releases.map((release) => <article key={release.version}><div><b>v{release.version}</b><time>{new Date(release.releasedAt).toLocaleDateString("de-CH")}</time></div><section><h3>{release.title}</h3><p>{release.summary}</p><ul>{release.changes.map((change) => <li key={change}>{change}</li>)}</ul></section></article>)}
      </section>
    </main>
  );
}

export function ImprintPage({ notice }: { notice: LegalNotice | null }) {
  const t = useI18n();

  if (!notice) {
    return <main className="page-shell imprint-page"><p className="eyebrow">{t("imprint.eyebrow", "Rechtliche Angaben")}</p><h1>{t("imprint.title", "Impressum")}</h1><p className="page-intro">{t("imprint.unavailable", "Die Anbieterangaben sind derzeit nicht verfügbar.")}</p></main>;
  }

  return (
    <main className="page-shell imprint-page">
      <p className="eyebrow">{t("imprint.eyebrow", "Rechtliche Angaben")}</p>
      <h1>{t("imprint.title", "Impressum")}</h1>
      <p className="page-intro">{t("imprint.intro", "Angaben gemäß § 5 DDG")}</p>

      <div className="imprint-overview">
        <section className="imprint-card">
          <MapPin />
          <div>
            <p className="eyebrow">{t("imprint.provider", "Diensteanbieter")}</p>
            <h2>{notice.providerName}</h2>
            <p>{notice.professionalTitle}</p>
            <address>{notice.street}<br />{notice.postalCode} {notice.city}<br />{notice.country}</address>
          </div>
        </section>
        <section className="imprint-card">
          <Mail />
          <div>
            <p className="eyebrow">{t("imprint.contact", "Kontakt")}</p>
            <h2>{t("imprint.contactTitle", "Direkt erreichbar")}</h2>
            <a href={`tel:${notice.phoneE164}`}><Phone />{notice.phone}</a>
            <a href={`mailto:${notice.email}`}><Mail />{notice.email}</a>
          </div>
        </section>
      </div>

      <div className="legal-sections">
        <section><h2>{t("imprint.vat.title", "Umsatzsteuer-Identifikationsnummer")}</h2><p>{t("imprint.vat.body", "Gemäß § 27a UStG: {vatId}", { vatId: notice.vatId })}</p></section>
        <section><h2>{t("imprint.profession.title", "Berufsrechtliche Regelungen")}</h2><p>{t("imprint.profession.body", "Tätigkeit als freiberuflicher IT-Berater. Es gelten keine spezifischen berufsrechtlichen Kammerzugehörigkeiten oder Zulassungsvoraussetzungen.")}</p></section>
        <section><h2>{t("imprint.responsible.title", "Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV")}</h2><address>{notice.responsibleName}<br />{notice.street}<br />{notice.postalCode} {notice.city}</address></section>
      </div>

      <section className="liability-section">
        <p className="eyebrow">{t("imprint.liability.eyebrow", "Rechtliche Hinweise")}</p>
        <h2>{t("imprint.liability.title", "Haftungsausschluss")}</h2>
        <article><h3>{t("imprint.content.title", "Haftung für Inhalte")}</h3><p>{t("imprint.content.body", "Die Inhalte dieser Website wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte kann jedoch keine Gewähr übernommen werden.")}</p></article>
        <article><h3>{t("imprint.links.title", "Haftung für Links")}</h3><p>{t("imprint.links.body", "Diese Website enthält Links zu externen Websites Dritter, auf deren Inhalte kein Einfluss besteht. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.")}</p></article>
        <article><h3>{t("imprint.copyright.title", "Urheberrecht")}</h3><p>{t("imprint.copyright.body", "Die durch den Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechts bedürfen der schriftlichen Zustimmung des jeweiligen Autors.")}</p></article>
      </section>

      <section className="dispute-section"><h2>{t("imprint.dispute.title", "Online-Streitbeilegung")}</h2><p>{t("imprint.dispute.body", "Die frühere europäische Plattform zur Online-Streitbeilegung wurde zum 20. Juli 2025 eingestellt. Zur Teilnahme an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle bin ich nicht verpflichtet und grundsätzlich nicht bereit.")}</p></section>
      <p className="imprint-updated">{t("imprint.updated", "Stand: 2026")}</p>
    </main>
  );
}
